<!-- eslint-disable max-lines -->
<template>
  <div class="content">
    <div class="row align-center">
      <div class="small-12 large-11 columns">
        <div class="content__title">
          {{ $t('FINANCE.SALES_CHART.TITLE') }}
        </div>
        <p v-if="$t('FINANCE.SALES_CHART.DESC')" class="content__description">
          {{ $t('FINANCE.SALES_CHART.DESC') }}
        </p>

        <div class="card">
          <div class="card__block">
            <div class="row align-justify">
              <div class="columns shrink">
                <span
                  class="pointer"
                  @click="openModal('salesFilterModalShow')"
                >
                  <i class="material-icons inline-center">
                    filter_list
                  </i>
                  <span class="show-for-large">
                    {{ $t('FINANCE.SALES_CHART.FILTER') }}
                  </span>
                </span>
              </div>

              <div class="columns">
                <span
                  class="chip chip--secondary chip--aligned-middle chip--mini"
                >
                  {{ $t(getChartModeLabel(chartMode)) }}
                </span>
                <span
                  class="chip chip--secondary chip--aligned-middle chip--mini"
                >
                  {{ translateDate(dateRange.from_date) }}
                  {{ $t('ARROW') }} {{ translateDate(dateRange.to_date) }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="!isChartLoading"
            class="card__block sales-charts-container"
          >
            <p v-if="apiError">
              {{ $t('FINANCE.SALES_CHART.API_ERROR') }}
            </p>
            <p v-else-if="noSalesData && !isChartLoading">
              {{ $t('FINANCE.SALES_CHART.NO_DATA') }}
            </p>

            <Highcharts
              v-else
              class="sales__chart"
              :options="salesChartOptions"
              dir="ltr"
            />
            <Highcharts
              v-if="!noRefundsData"
              class="sales__chart"
              :options="refundsChartOptions"
              dir="ltr"
            />
          </div>
          <template v-else>
            <div class="card__block card__block--loading" dir="ltr">
              <HollowDotsSpinner
                :animation-duration="1000"
                :dot-size="12"
                :dots-num="3"
                :color="'#95a5a6'"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
    <SalesChartFilterModal
      v-model="salesFilterModalShow"
      @chartModeChange="setMode"
      @fromDateChange="setFromDate"
      @toDateChange="setToDate"
      @updateFilter="updateChartData"
    />
  </div>
</template>

<script>
import moment from 'moment-jalaali';
import Zip from 'lodash.zip';

import HollowDotsSpinner from '@@/common/HollowDotsSpinner';

import layout from '@/mixins/layout';
import modal from '@/mixins/modal';
import chart from '@/mixins/chart';

import { DIRECTION } from '@/store/types';

import Color from '@/utils/color';
import { dateFormat } from '@/utils/date';
import lang from '@/mixins/lang';

import { Chart, requestQueue } from '@/api/api';
import getChartModeLabel from '@/utils/finance/salesChartModeTranslation';
import SalesChartFilterModal from './modal/SalesChartFilterModal';

import Highcharts from '@@/common/stats/Highcharts';

export default {
  name: 'SalesReports',

  components: {
    HollowDotsSpinner,
    SalesChartFilterModal,
    Highcharts,
  },

  mixins: [layout, modal, chart, lang],

  data() {
    return {
      dateRange: {
        from_date: '',
        to_date: '',
      },
      chartMode: 'disaggregated',
      noSalesData: true,
      noRefundsData: true,
      isChartLoading: true,
      salesSeries: [],
      salesKeys: [],
      refundsSeries: [],
      refundsKeys: [],
      salesFilterModalShow: false,
      apiError: false,

      defaultChartType: 'amount',
    };
  },

  computed: {
    dir() {
      return this.$store.getters[DIRECTION];
    },

    lastWeekDate() {
      const date = new Date();

      return date.setDate(date.getDate() - 7);
    },

    currentDate() {
      return Date.now();
    },

    salesGeneralSeries() {
      return aggregateNestedLists(
        this.salesSeries.map(item => item.data),
        Zip,
      );
    },

    refundsGeneralSeries() {
      return aggregateNestedLists(
        this.refundsSeries.map(item => item.data),
        Zip,
      );
    },

    salesChartSeries() {
      if (this.chartMode === 'disaggregated') {
        return this.salesSeries;
      }

      return [
        {
          name: this.$t('FINANCE.SALES_CHART.TOTAL_SALE'),
          data: this.salesGeneralSeries,
        },
      ];
    },

    refundsChartSeries() {
      if (this.chartMode === 'disaggregated') {
        return this.refundsSeries;
      }

      return [
        {
          name: this.$t('FINANCE.SALES_CHART.TOTAL_REFUNDS'),
          data: this.refundsGeneralSeries,
        },
      ];
    },

    salesChartOptions() {
      return {
        ...this.baseChartOptions,
        title: {
          text: this.$t('FINANCE.SALES_CHART.SALES'),
        },
        legend: {
          layout: 'horizontal',
          align: 'left',
        },
        colors: Color.get(this.salesChartSeries.length).byStep(2, 5),
        xAxis: {
          categories: this.salesKeys,
        },
        yAxis: {
          labels: {
            formatter: this.priceAxisFormatter(),
            useHTML: true,
          },
          title: {
            text: this.$t('FINANCE.SALES_CHART.AMOUNT'),
          },
        },
        series: this.salesChartSeries,
        tooltip: {
          formatter: this.tooltipFormatter(),
          shared: true,
          useHTML: true,
          padding: 2,
        },
      };
    },

    refundsChartOptions() {
      return {
        ...this.baseChartOptions,
        title: {
          text: this.$t('FINANCE.SALES_CHART.REFUNDS'),
        },
        legend: {
          layout: 'horizontal',
          align: 'left',
        },
        colors: Color.get(this.refundsChartSeries.length).byStep(2, 5),
        xAxis: {
          categories: this.refundsKeys,
        },
        yAxis: {
          labels: {
            formatter: this.priceAxisFormatter(),
            useHTML: true,
          },
          title: {
            text: this.$t('FINANCE.SALES_CHART.AMOUNT'),
          },
        },
        series: this.refundsChartSeries,
        tooltip: {
          formatter: this.tooltipFormatter(),
          shared: true,
          useHTML: true,
          padding: 2,
        },
      };
    },
  },

  created() {
    this.setPageTitle('FINANCE');
    this.dateRange = {
      from_date: moment(this.lastWeekDate).format('YYYY-MM-DD'),
      to_date: moment(this.currentDate).format('YYYY-MM-DD'),
    };

    this.getChartData(this.dateRange);
  },

  methods: {
    getChartModeLabel,
    getChartData(filters = {}) {
      Chart.get(filters)
        .then(
          ({
            body: {
              sales_chart: {
                keys: salesChartKeys = [],
                values: salesChartValues = [],
              },
              refunds_chart: {
                keys: refundsChartKeys = [],
                values: refundsChartValues = [],
              },
            },
          }) => {
            // if the keys and values are empty
            // a proper message will be shown instead`
            // otherwise the chart is shown
            this.noSalesData = ![salesChartKeys, salesChartValues].every(
              list => list.length,
            );
            this.noRefundsData = ![refundsChartKeys, refundsChartValues].every(
              list => list.length,
            );

            this.salesKeys = salesChartKeys;
            this.salesSeries = salesChartValues;
            this.refundsKeys = refundsChartKeys;
            this.refundsSeries = refundsChartValues;
            this.apiError = false;
          },
        )
        .then(() => {
          this.isChartLoading = false;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
          this.apiError = true;
        });
    },

    setMode(mode) {
      this.chartMode = mode;
    },

    updateChartData() {
      this.isChartLoading = true;
      requestQueue.flush();
      this.getChartData(this.dateRange);
      this.closeModal('salesFilterModalShow');
    },

    translateDate(date) {
      let toFormat = 'DD/MM/YYYY';

      if (this.lang === 'fa') {
        toFormat = 'jYYYY/jMM/jDD';
      }

      return dateFormat()(toFormat)(date);
    },

    setFromDate(selectedDate) {
      this.$set(this.dateRange, 'from_date', selectedDate);
    },

    setToDate(selectedDate) {
      this.$set(this.dateRange, 'to_date', selectedDate);
    },
  },
};

/**
 * aggregateNestedLists
 *
 * returns a list of numbers, each is the summation of first,
 * second, nth number of nested lists.
 *
 * @param lists a list of lists, containing numbers
 * @param zip   an implementation of zip function
 * @returns {Array|*}
 */
function aggregateNestedLists(lists, zip) {
  return zip(...lists).map(group => group.reduce((acc, value) => acc + value));
}
</script>

<style lang="scss">
.sales-charts-container {
  .el-loading-mask {
    z-index: 100;
  }
}

.sales {
  &__chart {
    &:first-child {
      margin-bottom: $global-margin * 2;
    }
  }
}
</style>
